import { ApiError } from "../api/client.js";
import type { DoorBanner } from "../ui/signin.js";
import type { Persona } from "./personas.js";

/**
 * DCTA-185: what the door says when a sign-in fails. Every demo bank busy — the login's 503 SERVICE_UNAVAILABLE with
 * the bank's Retry-After (every world in the middle of a signature) — is a calm wait with the bank's own number (at
 * least one second). P0-1 (DCTA-192): a customer the bank does not have — the login's 404 (prod's database had no
 * cus_dk005 while the door offered Daniel Koh) — is its own line that sends the customer to someone else; it is not
 * "can't reach the bank", which a retry would not cure. Anything else stays the door's "can't reach the bank". The
 * refusal's own words are never shown.
 */
export function doorFailure(error: unknown): DoorBanner {
  if (error instanceof ApiError && error.status === 503 && error.reasonCode === "SERVICE_UNAVAILABLE" && error.retryAfterSeconds !== undefined) {
    return { busy: Math.max(1, error.retryAfterSeconds) };
  }
  if (error instanceof ApiError && error.status === 404) return "unavailable";
  return "error";
}

/**
 * P0-1 (DCTA-192): the door's own customers that the bank lists (`GET /v1/session/personas`), in the door's order. Only
 * the ids are read: a name, a note or a customer the door has no words for never becomes a row. An answer it cannot use —
 * the read failed, a shape it does not know, nobody it has words for — leaves the door's own list, and a login the bank
 * then refuses says so (`doorFailure`).
 */
export function offered(all: readonly Persona[], answer: unknown): readonly Persona[] {
  const listed = listedIds(answer);
  const kept = all.filter((p) => listed.has(p.customerId));
  return kept.length === 0 ? all : kept;
}

function listedIds(answer: unknown): ReadonlySet<string> {
  const list: unknown = typeof answer === "object" && answer !== null ? (answer as { readonly personas?: unknown }).personas : undefined;
  if (!Array.isArray(list)) return new Set();
  const ids = list.map((p: unknown) => (typeof p === "object" && p !== null ? (p as { readonly customer_id?: unknown }).customer_id : undefined));
  return new Set(ids.filter((id): id is string => typeof id === "string"));
}
